import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { API_URL } from "../config/api";
import {
  AlertTriangle,
  Building2,
  RefreshCw,
  Upload,
  CheckCircle,
  Clock,
  Ban,
  Landmark,
  Plus,
  BarChart3,
  AlertCircle,
  Trash2,
  Settings,
  PiggyBank,
  ChevronDown,
  ChevronRight,
} from "lucide-react";


// Vertical colors
const VERTICAL_COLORS = {
  artcom: "bg-purple-100 text-purple-800 border-purple-300",
  cargo_cfs: "bg-green-100 text-green-800 border-green-300",
  ep_holding: "bg-orange-100 text-orange-800 border-orange-300",
  logistika: "bg-blue-100 text-blue-800 border-blue-300",
  hmm: "bg-pink-100 text-pink-800 border-pink-300",
  epac_hr: "bg-cyan-100 text-cyan-800 border-cyan-300",
  other: "bg-slate-100 text-slate-800 border-slate-300",
};

// Bank colors
const BANK_COLORS = {
  OTP: "bg-green-500",
  BKP: "bg-red-500", 
  NLB: "bg-blue-600",
  ADDIKO: "bg-orange-500",
  UNI: "bg-purple-600",
  ZABA: "bg-yellow-500",
  INTESA: "bg-emerald-600",
  SKB: "bg-indigo-500",
};

export default function BankStatusPage() {
  const { token, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("by-vertical");
  const [loading, setLoading] = useState(false);

  // Data
  const [config, setConfig] = useState({ verticals: [], all_banks: [], time_slots: [] });
  const [dashboard, setDashboard] = useState(null);
  const [byBankData, setByBankData] = useState(null);
  const [todaySnapshots, setTodaySnapshots] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  
  // Selected time slot for data entry
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");

  // Manual entry modal
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryData, setEntryData] = useState([]);

  // Bank config modal
  const [showBankConfigModal, setShowBankConfigModal] = useState(false);
  const [selectedVerticalForConfig, setSelectedVerticalForConfig] = useState(null);
  const [verticalBanks, setVerticalBanks] = useState([]);

  // Expanded verticals for showing bank breakdown
  const [expandedVerticals, setExpandedVerticals] = useState({});

  const toggleVerticalExpand = (verticalId) => {
    setExpandedVerticals(prev => ({
      ...prev,
      [verticalId]: !prev[verticalId]
    }));
  };

  // Fetch functions
  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-status/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(response.data);
      // Set default time slot based on current time
      const hour = new Date().getHours();
      if (hour < 10) setSelectedTimeSlot("08:30");
      else if (hour < 13) setSelectedTimeSlot("11:30");
      else setSelectedTimeSlot("15:00");
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-status/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Dashboard data received:", response.data);
      console.log("by_vertical:", response.data?.by_vertical);
      if (response.data?.by_vertical?.artcom) {
        console.log("artcom accounts:", response.data.by_vertical.artcom.accounts);
      }
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    }
  }, [token]);

  const fetchByBank = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-status/by-bank`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setByBankData(response.data);
    } catch (error) {
      console.error("Failed to fetch by-bank data:", error);
    }
  }, [token]);

  const fetchTodaySnapshots = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-status/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTodaySnapshots(response.data.snapshots || []);
    } catch (error) {
      console.error("Failed to fetch today's snapshots:", error);
    }
  }, [token]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-status/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    }
  }, [token]);

  const fetchLatest = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-status/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLatestSnapshot(response.data.snapshot);
    } catch (error) {
      console.error("Failed to fetch latest:", error);
    }
  }, [token]);

  // Refresh all data
  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchByBank(),
      fetchTodaySnapshots(),
      fetchAlerts(),
      fetchLatest(),
    ]);
    setLoading(false);
  };

  // Initial load
  useEffect(() => {
    fetchConfig();
    refreshAll();
  }, [fetchConfig]);

  // Import handler
  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/bank-status/import?time_slot=${selectedTimeSlot}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success(`Imported ${response.data.accounts_count} account balances`);
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  // Manual entry handler - uses vertical-specific banks
  const handleOpenEntryModal = () => {
    const initialData = [];
    
    // Use vertical-specific banks from config
    config.verticals.forEach(v => {
      const verticalBanks = v.banks || config.all_banks || [];
      
      // Check if we have data for this vertical in latestSnapshot
      const existingVertical = latestSnapshot?.verticals?.find(lv => lv.vertical_id === v.id);
      
      verticalBanks.forEach(bank => {
        const existingAccount = existingVertical?.accounts?.find(a => a.bank === bank);
        
        initialData.push({
          vertical: v.id,
          bank: bank,
          eur_balance: existingAccount?.eur_balance || 0,
          usd_balance: existingAccount?.usd_balance || 0,
          is_blocked: existingAccount?.is_blocked || false,
          blocked_amount: existingAccount?.blocked_amount || 0,
          pending_payables: existingAccount?.pending_payables || "",
          comment: existingAccount?.comment || "",
          comment_date: existingAccount?.comment_date || "",
        });
      });
    });
    
    setEntryData(initialData);
    setShowEntryModal(true);
  };

  const handleSaveEntries = async () => {
    // Group entries by vertical
    const verticals = {};
    entryData.forEach(entry => {
      if (!verticals[entry.vertical]) {
        verticals[entry.vertical] = [];
      }
      verticals[entry.vertical].push({
        bank: entry.bank,
        eur_balance: parseFloat(entry.eur_balance) || 0,
        usd_balance: parseFloat(entry.usd_balance) || 0,
        is_blocked: entry.is_blocked,
        blocked_amount: entry.is_blocked ? parseFloat(entry.blocked_amount) || 0 : null,
        pending_payables: entry.pending_payables || null,
        comment: entry.comment || null,
        comment_date: entry.comment_date || null,
      });
    });

    const snapshotData = {
      date: new Date().toISOString().split("T")[0],
      time_slot: selectedTimeSlot,
      verticals: Object.entries(verticals).map(([vertical_id, accounts]) => ({
        vertical_id,
        accounts,
      })),
    };

    setLoading(true);
    try {
      await axios.post(`${API_URL}/bank-status/`, snapshotData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Bank status saved");
      setShowEntryModal(false);
      refreshAll();
    } catch (error) {
      toast.error("Failed to save bank status");
    } finally {
      setLoading(false);
    }
  };

  const updateEntryField = (index, field, value) => {
    const newData = [...entryData];
    newData[index] = { ...newData[index], [field]: value };
    setEntryData(newData);
  };

  // Bank config handlers
  const openBankConfigModal = (vertical) => {
    setSelectedVerticalForConfig(vertical);
    setVerticalBanks(vertical.banks || []);
    setShowBankConfigModal(true);
  };

  const toggleBankForVertical = (bank) => {
    if (verticalBanks.includes(bank)) {
      setVerticalBanks(verticalBanks.filter(b => b !== bank));
    } else {
      setVerticalBanks([...verticalBanks, bank]);
    }
  };

  const saveBankConfig = async () => {
    try {
      await axios.post(
        `${API_URL}/bank-status/config/verticals/${selectedVerticalForConfig.id}/banks`,
        verticalBanks,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bank configuration saved");
      setShowBankConfigModal(false);
      await fetchConfig();
      
      // Refresh entry data with new bank configuration
      if (showEntryModal && selectedTimeSlot) {
        // Re-initialize entry data after config change
        setTimeout(() => {
          openEntryModal(selectedTimeSlot);
        }, 500);
      }
    } catch (error) {
      toast.error("Failed to save bank config");
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency,
    }).format(amount || 0);
  };

  // Get vertical name
  const getVerticalName = (id) => {
    const v = config.verticals.find(v => v.id === id);
    return v ? v.name : id;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="bank-status-title">
                <Landmark className="h-6 w-6" />
                Bank Account Status (TRR)
              </h1>
              <p className="text-emerald-200 text-sm mt-1">
                {dashboard?.latest_date ? (
                  <>Last updated: <span className="font-semibold text-white">{new Date(dashboard.latest_date).toLocaleDateString('sl-SI')} at {dashboard.latest_time || '--'}</span></>
                ) : (
                  'Daily balance tracking - 3x per day (08:30, 11:30, 15:00)'
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Time slot selector */}
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                <Clock className="h-4 w-4" />
                <Select value={selectedTimeSlot} onValueChange={setSelectedTimeSlot}>
                  <SelectTrigger className="w-[100px] border-0 bg-transparent text-white focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.time_slots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isAdmin && (
                <>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".msg"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleImport(e.target.files[0]);
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      asChild
                    >
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Import .msg
                      </span>
                    </Button>
                  </label>
                  <Button
                    size="sm"
                    onClick={handleOpenEntryModal}
                    className="bg-white text-emerald-700 hover:bg-emerald-50"
                    data-testid="manual-entry-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Manual Entry
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={refreshAll}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total EUR Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">
                {formatCurrency(dashboard?.totals?.total_eur || byBankData?.totals?.total_eur)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total USD Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {formatCurrency(dashboard?.totals?.total_usd || byBankData?.totals?.total_usd || 0, "USD")}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Blocked Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${(dashboard?.blocked_accounts?.length || byBankData?.totals?.blocked_count) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {dashboard?.blocked_accounts?.length || byBankData?.totals?.blocked_count || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Pending Payables</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${(dashboard?.pending_payables?.length) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {dashboard?.pending_payables?.length || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert Banner */}
        {(dashboard?.blocked_accounts?.length > 0 || byBankData?.totals?.blocked_count > 0) && (
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 flex items-center gap-4 mb-6">
            <Ban className="h-8 w-8 text-red-600" />
            <div>
              <h3 className="font-bold text-red-800">Blocked Accounts Detected!</h3>
              <p className="text-red-700">
                {dashboard?.blocked_accounts?.length || byBankData?.totals?.blocked_count} account(s) are currently blocked
              </p>
            </div>
            <Button 
              className="ml-auto bg-red-600 hover:bg-red-700"
              onClick={() => setActiveTab("alerts")}
            >
              View Details
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="by-vertical" className="gap-2" data-testid="tab-by-vertical">
              <Building2 className="h-4 w-4" />
              By Company
            </TabsTrigger>
            <TabsTrigger value="by-bank" className="gap-2" data-testid="tab-by-bank">
              <PiggyBank className="h-4 w-4" />
              By Bank
            </TabsTrigger>
            <TabsTrigger value="alerts" className="gap-2" data-testid="tab-alerts">
              <AlertTriangle className="h-4 w-4" />
              Alerts
              {alerts.length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white">{alerts.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* By Vertical Tab */}
          <TabsContent value="by-vertical">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Balance by Company</CardTitle>
                    <CardDescription>Current status per vertical entity</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {config.verticals.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                ) : (
                  <div className="space-y-3">
                    {config.verticals.map((vertical) => {
                      const data = dashboard?.by_vertical?.[vertical.id];
                      const verticalBankList = vertical.banks || [];
                      const isExpanded = expandedVerticals[vertical.id];
                      
                      return (
                        <div key={vertical.id} className={`rounded-lg border overflow-hidden ${VERTICAL_COLORS[vertical.id] || VERTICAL_COLORS.other}`}>
                          {/* Header - Clickable */}
                          <div 
                            className="p-4 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => toggleVerticalExpand(vertical.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5" />
                                ) : (
                                  <ChevronRight className="h-5 w-5" />
                                )}
                                <Building2 className="h-5 w-5" />
                                <h3 className="font-bold text-lg">{vertical.name}</h3>
                                {vertical.salary_day && (
                                  <Badge variant="outline" className="text-xs">
                                    Salary: {vertical.salary_day}th
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  Banks: {verticalBankList.join(", ")}
                                </Badge>
                                {data?.blocked > 0 && (
                                  <Badge className="bg-red-500 text-white">
                                    {data.blocked} blocked
                                  </Badge>
                                )}
                                {isAdmin && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openBankConfigModal(vertical);
                                    }}
                                  >
                                    <Settings className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold">{formatCurrency(data?.eur || 0)}</div>
                                {(data?.usd || 0) > 0 && (
                                  <div className="text-sm opacity-75">{formatCurrency(data?.usd, "USD")}</div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Bank breakdown - Expandable */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-current/20">
                              <div className="pt-3">
                                <h4 className="text-sm font-medium mb-3 opacity-75">Bank Account Balances</h4>
                                {data?.accounts?.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="bg-white/50">
                                          <th className="text-left p-2 font-medium text-slate-600">Bank</th>
                                          <th className="text-right p-2 font-medium text-slate-600">EUR</th>
                                          <th className="text-right p-2 font-medium text-blue-600">USD</th>
                                          <th className="text-center p-2 font-medium text-slate-600">Status</th>
                                          <th className="text-left p-2 font-medium text-slate-600">ČAKA / Comment</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {data.accounts.map((acc, idx) => (
                                          <tr key={idx} className="border-b border-current/10 last:border-0">
                                            <td className="p-2">
                                              <div className="flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${BANK_COLORS[acc.bank] || 'bg-slate-400'}`}></span>
                                                <span className="font-bold" style={{ color: acc.bank === 'OTP' ? '#22c55e' : acc.bank === 'BKP' ? '#eab308' : acc.bank === 'NLB' ? '#3b82f6' : acc.bank === 'ADDIKO' ? '#f97316' : '#6b7280' }}>
                                                  {acc.bank}
                                                </span>
                                              </div>
                                            </td>
                                            <td className={`p-2 text-right font-bold ${acc.eur_balance < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                                              {formatCurrency(acc.eur_balance)}
                                            </td>
                                            <td className={`p-2 text-right font-medium ${acc.usd_balance > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                                              {acc.usd_balance > 0 ? formatCurrency(acc.usd_balance, "USD") : "-"}
                                            </td>
                                            <td className="p-2 text-center">
                                              {acc.is_blocked ? (
                                                <span className="text-red-600 font-bold">BLOKADA!</span>
                                              ) : (
                                                <span className="text-green-600">OK</span>
                                              )}
                                            </td>
                                            <td className="p-2 text-left text-slate-600">
                                              {acc.pending_payables && (
                                                <span className="text-red-600 mr-2">ČAKA {acc.pending_payables}</span>
                                              )}
                                              {acc.comment && (
                                                <span>
                                                  {acc.comment_date && <span className="text-slate-500">{acc.comment_date} </span>}
                                                  {acc.comment}
                                                </span>
                                              )}
                                              {!acc.pending_payables && !acc.comment && <span className="text-slate-400">-</span>}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="text-sm opacity-60">No bank data recorded yet</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* By Bank Tab */}
          <TabsContent value="by-bank">
            <Card>
              <CardHeader>
                <CardTitle>Balance by Bank</CardTitle>
                <CardDescription>All accounts grouped by bank across all companies</CardDescription>
              </CardHeader>
              <CardContent>
                {!byBankData?.by_bank || Object.keys(byBankData.by_bank).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(byBankData.by_bank).map(([bankName, bankData]) => (
                      <div key={bankName} className="p-4 rounded-lg border bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`w-4 h-4 rounded-full ${BANK_COLORS[bankName] || 'bg-slate-400'}`}></span>
                            <h3 className="font-bold text-lg">{bankName}</h3>
                            {bankData.blocked_count > 0 && (
                              <Badge className="bg-red-500 text-white">
                                {bankData.blocked_count} blocked ({formatCurrency(bankData.blocked_amount)})
                              </Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-emerald-600">{formatCurrency(bankData.total_eur)}</div>
                            {bankData.total_usd > 0 && (
                              <div className="text-sm text-blue-600">{formatCurrency(bankData.total_usd, "USD")}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Verticals breakdown within bank */}
                        <div className="mt-3 pt-3 border-t">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {bankData.verticals.map((v, idx) => (
                              <div 
                                key={idx} 
                                className={`rounded p-2 text-sm ${VERTICAL_COLORS[v.vertical_id] || VERTICAL_COLORS.other}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{v.vertical_short || v.vertical_name}</span>
                                  {v.is_blocked && <Ban className="h-3 w-3 text-red-500" />}
                                </div>
                                <div className={`font-bold ${v.eur_balance < 0 ? 'text-red-600' : ''}`}>
                                  {formatCurrency(v.eur_balance)}
                                </div>
                                {v.pending_payables && (
                                  <div className="text-xs text-orange-600 truncate">{v.pending_payables}</div>
                                )}
                                {v.comment && (
                                  <div className="text-xs text-slate-600 mt-1">
                                    {v.comment}
                                    {v.comment_date && <span className="ml-1">({v.comment_date})</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card className={alerts.some(a => a.severity === 'critical') ? "border-red-200" : ""}>
              <CardHeader className={alerts.some(a => a.severity === 'critical') ? "bg-red-50" : ""}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className={`h-5 w-5 ${alerts.some(a => a.severity === 'critical') ? 'text-red-600' : 'text-slate-400'}`} />
                      Bank Account Alerts
                    </CardTitle>
                    <CardDescription>
                      Blocked accounts, low balances, and pending payables
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchAlerts}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {alerts.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                    <p className="text-slate-500">All accounts in good standing - no alerts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border-2 ${
                          alert.severity === 'critical' ? 'bg-red-50 border-red-300' :
                          alert.severity === 'high' ? 'bg-orange-50 border-orange-300' :
                          alert.severity === 'medium' ? 'bg-yellow-50 border-yellow-300' :
                          'bg-blue-50 border-blue-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {alert.type === 'blocked' && <Ban className="h-5 w-5 text-red-600" />}
                            {alert.type === 'negative_balance' && <AlertCircle className="h-5 w-5 text-orange-600" />}
                            {alert.type === 'zero_balance' && <AlertCircle className="h-5 w-5 text-yellow-600" />}
                            {alert.type === 'pending' && <Clock className="h-5 w-5 text-blue-600" />}
                            <div>
                              <div className="font-bold">{alert.vertical} - {alert.bank}</div>
                              <div className="text-sm">{alert.message}</div>
                            </div>
                            <Badge className={
                              alert.severity === 'critical' ? 'bg-red-500 text-white' :
                              alert.severity === 'high' ? 'bg-orange-500 text-white' :
                              alert.severity === 'medium' ? 'bg-yellow-500 text-white' :
                              'bg-blue-100 text-blue-700'
                            }>
                              {alert.severity}
                            </Badge>
                          </div>
                          {alert.balance !== undefined && (
                            <div className="text-right">
                              <div className={`text-xl font-bold ${alert.balance < 0 ? 'text-red-600' : ''}`}>
                                {formatCurrency(alert.balance)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Manual Entry Modal */}
      <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enter Bank Balances - {selectedTimeSlot}</DialogTitle>
            <DialogDescription>
              Enter EUR and USD balances for accounts at {selectedTimeSlot} snapshot. Each company shows only its assigned banks.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {config.verticals.map((vertical) => {
              const verticalEntries = entryData.filter(e => e.vertical === vertical.id);
              if (verticalEntries.length === 0) return null;
              
              return (
                <div key={vertical.id} className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <h3 className={`font-bold text-lg px-3 py-2 rounded ${VERTICAL_COLORS[vertical.id] || VERTICAL_COLORS.other}`}>
                        {vertical.name}
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openBankConfigModal(vertical); }}
                        className="p-1 h-7 hover:bg-slate-200"
                        title="Add or remove banks for this company"
                        data-testid={`config-banks-${vertical.id}`}
                      >
                        <Settings className="h-4 w-4 text-slate-500" />
                      </Button>
                    </div>
                    <span className="text-xs text-slate-500">Banks: {vertical.banks?.join(", ")}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 px-2">
                      <div className="col-span-2">Bank</div>
                      <div className="col-span-2">EUR Balance</div>
                      <div className="col-span-2">USD Balance</div>
                      <div className="col-span-1">Blocked</div>
                      <div className="col-span-2">Pending</div>
                      <div className="col-span-3">Comment</div>
                    </div>
                    {verticalEntries.map((entry) => {
                      const globalIdx = entryData.findIndex(e => e.vertical === vertical.id && e.bank === entry.bank);
                      return (
                        <div key={entry.bank} className="grid grid-cols-12 gap-2 items-center p-2 hover:bg-slate-50 rounded border">
                          <div className="col-span-2 font-medium flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${BANK_COLORS[entry.bank] || 'bg-slate-400'}`}></span>
                            {entry.bank}
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={entry.eur_balance}
                              onChange={(e) => updateEntryField(globalIdx, "eur_balance", e.target.value)}
                              placeholder="0.00 €"
                              className="text-right h-8"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={entry.usd_balance || ""}
                              onChange={(e) => updateEntryField(globalIdx, "usd_balance", e.target.value)}
                              placeholder="0.00 $"
                              className="text-right h-8 text-blue-600"
                            />
                          </div>
                          <div className="col-span-1 flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={entry.is_blocked}
                              onChange={(e) => updateEntryField(globalIdx, "is_blocked", e.target.checked)}
                              className="h-4 w-4"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              value={entry.pending_payables}
                              onChange={(e) => updateEntryField(globalIdx, "pending_payables", e.target.value)}
                              placeholder="ČAKA..."
                              className="h-8"
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              value={entry.comment}
                              onChange={(e) => updateEntryField(globalIdx, "comment", e.target.value)}
                              placeholder="Note..."
                              className="h-8"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntries} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? "Saving..." : "Save Snapshot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Config Modal */}
      <Dialog open={showBankConfigModal} onOpenChange={setShowBankConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configure Banks for {selectedVerticalForConfig?.name}</DialogTitle>
            <DialogDescription>
              Select which banks this company uses
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="grid grid-cols-2 gap-2">
              {(config.all_banks || []).map((bank) => (
                <div 
                  key={bank}
                  onClick={() => toggleBankForVertical(bank)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    verticalBanks.includes(bank) 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${BANK_COLORS[bank] || 'bg-slate-400'}`}></span>
                    <span className="font-medium">{bank}</span>
                    {verticalBanks.includes(bank) && (
                      <CheckCircle className="h-4 w-4 text-emerald-500 ml-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankConfigModal(false)}>
              Cancel
            </Button>
            <Button onClick={saveBankConfig} className="bg-emerald-600 hover:bg-emerald-700">
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
